import type { CountryListItem as ApiCountry } from '../../api/currencyCountries'
import EnglishDatePicker from '../../components/common/EnglishDatePicker'
import {
  SHAREHOLDER_WIZARD_ROLE_BENEFICIAL_OWNER_ONLY,
  STAKEHOLDER_ROLE_OPTIONS,
  STAKEHOLDER_ROLE_OPTIONS_SHAREHOLDER_ONLY,
  type IndividualStakeholderRow,
  type StakeholderIdentityType,
  type StakeholderPersonRole,
} from './businessStakeholderTypes'

type Props = {
  busy: boolean
  row: IndividualStakeholderRow
  countryOptions: ApiCountry[]
  referenceCountries: ApiCountry[]
  countriesLoadError: boolean
  onPatch: (p: Partial<IndividualStakeholderRow>) => void
  partyTypeDisabledLegal?: boolean
  /** Shareholders questionnaire step vs additional directors step */
  variant?: 'shareholder' | 'default'
  /** Directors who are not shareholders — hide ownership % */
  hidePercentShares?: boolean
}

function CountrySelectLite(props: {
  label: string
  value: string
  disabled: boolean
  countryOptions: ApiCountry[]
  referenceCountries: ApiCountry[]
  countriesLoadError: boolean
  onChange: (v: string) => void
}) {
  const {
    label,
    value,
    disabled,
    countryOptions,
    referenceCountries,
    countriesLoadError,
    onChange,
  } = props
  const inCatalogue = countryOptions.some((c) => c.name === value)
  if (countriesLoadError && referenceCountries.length === 0) {
    return (
      <label className="block text-sm">
        <span className="text-[#627396]">{label}</span>
        <input
          className="mt-1 w-full rounded-lg border px-3 py-2"
          style={{ borderColor: '#C1C7D3' }}
          disabled={disabled}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type country name"
        />
      </label>
    )
  }
  return (
    <label className="block text-sm">
      <span className="text-[#627396]">{label}</span>
      <select
        className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm"
        style={{ borderColor: '#C1C7D3' }}
        disabled={disabled || (referenceCountries.length === 0 && !countriesLoadError)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">
          {referenceCountries.length === 0 && !countriesLoadError ? 'Loading…' : 'Select country…'}
        </option>
        {value && !inCatalogue ? (
          <option value={value}>
            {value} (saved)
          </option>
        ) : null}
        {countryOptions.map((c) => (
          <option key={c.id} value={c.name}>
            {c.name}
          </option>
        ))}
      </select>
    </label>
  )
}

export function IndividualStakeholderFormFields({
  busy,
  row,
  countryOptions,
  referenceCountries,
  countriesLoadError,
  onPatch,
  partyTypeDisabledLegal = true,
  variant = 'default',
  hidePercentShares = false,
}: Props) {
  const lockBoRole = SHAREHOLDER_WIZARD_ROLE_BENEFICIAL_OWNER_ONLY && variant === 'shareholder'
  const roleOptions = lockBoRole ? STAKEHOLDER_ROLE_OPTIONS_SHAREHOLDER_ONLY : STAKEHOLDER_ROLE_OPTIONS
  const txt = (
    label: string,
    field:
      | keyof Pick<
          IndividualStakeholderRow,
          | 'firstName'
          | 'lastName'
          | 'middleName'
          | 'taxIdPersonal'
          | 'identityDocumentNumber'
          | 'residenceAddress'
        >,
    opts?: { placeholder?: string; type?: string }
  ) => (
    <label className="block text-sm sm:col-span-2">
      <span className="text-[#627396]">{label}</span>
      <input
        disabled={busy}
        type={opts?.type ?? 'text'}
        className="mt-1 w-full rounded-lg border px-3 py-2"
        style={{ borderColor: '#C1C7D3' }}
        value={String(row[field] ?? '')}
        placeholder={opts?.placeholder}
        onChange={(e) => onPatch({ [field]: e.target.value })}
      />
    </label>
  )

  const dateField = (
    label: string,
    field: keyof Pick<IndividualStakeholderRow, 'dateOfBirth' | 'identityIssueDate' | 'identityExpiryDate'>,
    opts?: { disabled?: boolean }
  ) => (
    <label className="block text-sm">
      <span className="text-[#627396]">{label}</span>
      <EnglishDatePicker
        disabled={busy || opts?.disabled}
        className="mt-1 w-full rounded-lg border px-3 py-2"
        style={{ borderColor: '#C1C7D3' }}
        value={String(row[field] ?? '')}
        onChange={(value) => onPatch({ [field]: value })}
      />
    </label>
  )

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="block text-sm sm:col-span-2">
        <span className="text-[#627396]">Type</span>
        <select
          disabled={busy || partyTypeDisabledLegal}
          className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
          style={{ borderColor: '#C1C7D3' }}
          value={row.partyType}
          onChange={(e) => {
            const v = e.target.value as IndividualStakeholderRow['partyType']
            if (v === 'individual') onPatch({ partyType: v })
          }}
        >
          <option value="individual">Individual</option>
          <option value="legal_entity" disabled>
            Legal entity (coming soon)
          </option>
        </select>
      </label>
      <label className="block text-sm sm:col-span-2">
        <span className="text-[#627396]">Role</span>
        <select
          disabled={busy || lockBoRole}
          className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
          style={{ borderColor: '#C1C7D3' }}
          value={row.role}
          onChange={(e) =>
            onPatch({
              role: e.target.value as StakeholderPersonRole,
              syncedLinks: undefined,
            })
          }
        >
          {roleOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      {txt('Legal first name', 'firstName')}
      {txt('Legal last name', 'lastName')}
      {txt('Legal middle name (optional)', 'middleName')}
      {dateField('Date of birth', 'dateOfBirth')}
      <CountrySelectLite
        label="Tax country"
        value={String(row.taxCountry ?? '')}
        disabled={busy}
        countryOptions={countryOptions}
        referenceCountries={referenceCountries}
        countriesLoadError={countriesLoadError}
        onChange={(v) => onPatch({ taxCountry: v })}
      />
      <label className="block text-sm sm:col-span-2">
        <span className="text-[#627396]">TAX ID (personal)</span>
        <input
          disabled={busy}
          type="text"
          className="mt-1 w-full rounded-lg border px-3 py-2"
          style={{ borderColor: '#C1C7D3' }}
          value={String(row.taxIdPersonal ?? '')}
          onChange={(e) => onPatch({ taxIdPersonal: e.target.value })}
        />
      </label>
      <CountrySelectLite
        label="Residence country"
        value={String(row.residenceCountry ?? '')}
        disabled={busy}
        countryOptions={countryOptions}
        referenceCountries={referenceCountries}
        countriesLoadError={countriesLoadError}
        onChange={(v) => onPatch({ residenceCountry: v })}
      />
      <label className="block text-sm sm:col-span-2">
        <span className="text-[#627396]">Residence address</span>
        <textarea
          disabled={busy}
          rows={2}
          className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: '#C1C7D3' }}
          value={String(row.residenceAddress ?? '')}
          onChange={(e) => onPatch({ residenceAddress: e.target.value })}
        />
      </label>
      <label className="block text-sm">
        <span className="text-[#627396]">Identity type</span>
        <select
          disabled={busy}
          className="mt-1 w-full rounded-lg border bg-white px-3 py-2"
          style={{ borderColor: '#C1C7D3' }}
          value={row.identityType}
          onChange={(e) =>
            onPatch({
              identityType: e.target.value as StakeholderIdentityType,
            })
          }
        >
          <option value="id_card">ID card</option>
          <option value="passport">Passport</option>
        </select>
      </label>
      {txt('Identity document number', 'identityDocumentNumber')}
      <div className="min-w-0 space-y-4 sm:col-span-2">
        {dateField('Identity document issue date', 'identityIssueDate')}

        <div className="min-w-0">
          <span className="text-sm text-[#627396]">Identity document expiry date</span>
          <div className="mt-1 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-4">
            {row.identityExpiryIndefinite ? (
              <div
                className="flex flex-1 items-center rounded-lg border border-dashed px-3 py-2 text-sm text-[#627396]"
                style={{ borderColor: '#C1C7D3', background: '#FAFBFC' }}
              >
                Not applicable — indefinite / lifetime validity (no expiry date).
              </div>
            ) : (
              <EnglishDatePicker
                disabled={busy}
                containerClassName="flex-1 min-w-0 sm:max-w-[min(100%,16rem)]"
                className="box-border h-[42px] min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm sm:max-w-[min(100%,16rem)]"
                style={{ borderColor: '#C1C7D3' }}
                value={String(row.identityExpiryDate ?? '')}
                onChange={(value) => onPatch({ identityExpiryDate: value })}
              />
            )}
            <label
              className="flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm shadow-sm transition-colors hover:bg-[#F8F9FB] sm:h-[42px] sm:self-center sm:py-0"
              style={{ borderColor: '#C1C7D3' }}
            >
              <input
                type="checkbox"
                disabled={busy}
                className="h-4 w-4 shrink-0 rounded border-[#C1C7D3]"
                checked={row.identityExpiryIndefinite === true}
                onChange={(e) => {
                  const checked = e.target.checked
                  onPatch({
                    identityExpiryIndefinite: checked,
                    ...(checked ? { identityExpiryDate: '' } : {}),
                  })
                }}
              />
              <span className="min-w-0 font-medium leading-tight text-[#28293C]">No expiry date</span>
            </label>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-[#627396]">
            Lifetime-valid documents: tick above and skip entering an expiry date.
          </p>
        </div>
      </div>
      {!hidePercentShares ? (
        <label className="block text-sm sm:col-span-2">
          <span className="text-[#627396]">% of shares</span>
          <input
            disabled={busy}
            type="number"
            min={0}
            max={100}
            step="any"
            className="mt-1 w-full rounded-lg border px-3 py-2"
            style={{ borderColor: '#C1C7D3' }}
            value={String(row.percentShares ?? '')}
            placeholder="e.g. 42.5"
            onChange={(e) => onPatch({ percentShares: e.target.value })}
            onBlur={() => {
              const raw = String(row.percentShares ?? '').trim()
              if (!raw) return
              const n = Number(raw.replace(',', '.'))
              if (!Number.isFinite(n)) return
              let next = raw
              if (n > 100) next = '100'
              else if (n < 0) next = '0'
              if (next !== raw) onPatch({ percentShares: next })
            }}
          />
        </label>
      ) : null}
    </div>
  )
}
